
import { Facebook, Instagram, ExternalLink } from "lucide-react";

interface SocialMediaLinksProps {
  instagramHandle?: string;
  facebookHandle?: string;
  tiktokHandle?: string;
  pinterestHandle?: string;
  onSocialMediaClick: (platform: string, handle: string) => void;
}

const TikTokIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
  >
    <path
      d="M16.6 5.82C15.9165 5.03962 15.5397 4.03743 15.54 3H12.45V15.4C12.4262 16.071 12.1429 16.7066 11.6598 17.1729C11.1767 17.6393 10.5309 17.8999 9.86 17.91C8.44 17.91 7.26 16.75 7.26 15.3C7.26 13.85 8.45 12.69 9.86 12.69C10.1912 12.6854 10.5189 12.7512 10.82 12.88V9.73C10.1891 9.60557 9.54076 9.58772 8.90281 9.67757C8.26486 9.76742 7.64974 9.96338 7.08751 10.2575C6.52528 10.5516 6.02524 10.9391 5.61141 11.403C5.19757 11.8669 4.87672 12.3986 4.66598 12.9726C4.45523 13.5466 4.35814 14.1527 4.37891 14.7607C4.39969 15.3686 4.53803 15.9683 4.78631 16.5299C5.03458 17.0915 5.38873 17.6047 5.82935 18.0446C6.26997 18.4845 6.78977 18.8437 7.36252 19.1043C7.93527 19.3649 8.55161 19.5221 9.17967 19.5676C9.80774 19.6132 10.4399 19.5464 11.0452 19.3705C11.6505 19.1946 12.2183 18.9127 12.7224 18.5382C13.2265 18.1637 13.6582 17.7029 13.9962 17.1778C14.3341 16.6527 14.5726 16.0726 14.7 15.46V8.92C15.6839 9.85842 16.9079 10.5176 18.24 10.82V7.67C17.6593 7.52782 17.1167 7.26251 16.6445 6.88939C16.1724 6.51627 15.7789 6.0425 15.49 5.5L16.6 5.82Z"
      fill="currentColor"
    />
  </svg>
);

const PinterestIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="w-6 h-6"
  >
    <path
      d="M12 0a12 12 0 0 0-4 23.26c0-.61 0-1.36.16-2a13.25 13.25 0 0 0 2.88-5.39c.28 0 .54-.08 1.08-.08 4 0 6.64 3.56 6.64 8.24 0 3.48-1.84 6-4.36 6-2.92 0-5.44-2.84-5.44-6.84 0-2.76 1.16-4.64 2.84-4.64 1.32 0 2.08 1 2.08 2.36 0 1.44-.92 3.56-.92 3.56a7.84 7.84 0 0 0 2.76 7.32c-.36 1.2-.72 2.4-1 3.32A12 12 0 1 0 12 0Z"
      fill="currentColor"
    />
  </svg>
);

export const SocialMediaLinks = ({
  instagramHandle,
  facebookHandle,
  tiktokHandle,
  pinterestHandle,
}: SocialMediaLinksProps) => {
  if (!instagramHandle && !facebookHandle && !tiktokHandle && !pinterestHandle) return null;

  const handleSocialClick = (platform: string, handle: string) => {
    let url = '';
    switch (platform) {
      case 'instagram':
        url = `https://instagram.com/${handle.replace('@', '')}`;
        break;
      case 'facebook':
        url = handle.startsWith('http') ? handle : `https://facebook.com/${handle}`;
        break;
      case 'tiktok':
        url = `https://tiktok.com/@${handle.replace('@', '')}`;
        break;
      case 'pinterest':
        url = `https://pinterest.com/${handle.replace('@', '')}`;
        break;
    }
    if (url) window.open(url, '_blank');
  };

  return (
    <div className="flex gap-4 mt-4 justify-center md:justify-start">
      {instagramHandle && (
        <button
          onClick={() => handleSocialClick('instagram', instagramHandle)}
          className="text-pink-600 hover:text-pink-700"
          title={`Follow on Instagram`}
        >
          <Instagram className="w-6 h-6" />
        </button>
      )}
      {facebookHandle && (
        <button
          onClick={() => handleSocialClick('facebook', facebookHandle)}
          className="text-blue-600 hover:text-blue-700"
          title={`Follow on Facebook`}
        >
          <Facebook className="w-6 h-6" />
        </button>
      )}
      {tiktokHandle && (
        <button
          onClick={() => handleSocialClick('tiktok', tiktokHandle)}
          className="text-black hover:text-gray-700"
          title={`Follow on TikTok`}
        >
          <TikTokIcon />
        </button>
      )}
      {pinterestHandle && (
        <button
          onClick={() => handleSocialClick('pinterest', pinterestHandle)}
          className="text-red-600 hover:text-red-700"
          title={`Follow on Pinterest`}
        >
          <PinterestIcon />
        </button>
      )}
    </div>
  );
};
